import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  InvitationResponse,
  InvitationPageResponse,
  CreateInvitationRequest,
  RegisterWithInvitationRequest,
  UpdateInvitationRoleRequest,
  RoleResponse,
  InvitationFilters,
} from '../models/invitation.model';

@Injectable({
  providedIn: 'root',
})
export class InvitationApiService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = `${environment.apiUrl}api`;

  // GET /api/invitations?page=&size=[&businessId=]
  // `businessId` solo lo respeta el backend para el Super Admin (cross-tenant); el
  // resto de roles siempre opera sobre el negocio del JWT.
  listInvitations(
    page = 0,
    size = 20,
    filters?: InvitationFilters,
  ): Observable<InvitationPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());

    if (filters?.businessId !== undefined) {
      params = params.set('businessId', filters.businessId.toString());
    }

    if (filters?.email) {
      params = params.set('email', filters.email);
    }

    if (filters?.roleId !== undefined) {
      params = params.set('roleId', filters.roleId.toString());
    }

    if (filters?.status) {
      params = params.set('status', filters.status);
    }

    if (filters?.createdFrom) {
      params = params.set(
        'createdFrom',
        filters.createdFrom instanceof Date
          ? filters.createdFrom.toISOString()
          : filters.createdFrom,
      );
    }

    if (filters?.createdTo) {
      params = params.set(
        'createdTo',
        filters.createdTo instanceof Date
          ? filters.createdTo.toISOString()
          : filters.createdTo,
      );
    }

    return this.http.get<InvitationPageResponse>(`${this.apiUrl}/invitations`, {
      params,
    });
  }

  // POST /api/invitations
  createInvitation(
    request: CreateInvitationRequest,
  ): Observable<InvitationResponse> {
    return this.http.post<InvitationResponse>(
      `${this.apiUrl}/invitations`,
      request,
    );
  }

  // GET /api/invitations/{id}[?businessId=]
  getInvitationById(
    id: number,
    businessId?: number,
  ): Observable<InvitationResponse> {
    let params = new HttpParams();
    if (businessId !== undefined)
      params = params.set('businessId', businessId.toString());
    return this.http.get<InvitationResponse>(
      `${this.apiUrl}/invitations/${id}`,
      { params },
    );
  }

  // POST /api/invitations/{id}/resend[?businessId=]  (regenera token + renueva expiración) — PENDING/EXPIRED
  // `businessId` permite al Super Admin reenviar la invitación de OTRA empresa
  // (p.ej. desde el formulario/detalle de Businesses).
  resendInvitation(
    id: number,
    businessId?: number,
  ): Observable<InvitationResponse> {
    let params = new HttpParams();
    if (businessId !== undefined)
      params = params.set('businessId', businessId.toString());
    return this.http.post<InvitationResponse>(
      `${this.apiUrl}/invitations/${id}/resend`,
      {},
      { params },
    );
  }

  // PATCH /api/invitations/{id}/role  — PENDING/EXPIRED
  updateInvitationRole(
    id: number,
    request: UpdateInvitationRoleRequest,
  ): Observable<InvitationResponse> {
    return this.http.patch<InvitationResponse>(
      `${this.apiUrl}/invitations/${id}/role`,
      request,
    );
  }

  // DELETE /api/invitations/{id}  (revoke)
  revokeInvitation(id: number): Observable<InvitationResponse> {
    return this.http.delete<InvitationResponse>(
      `${this.apiUrl}/invitations/${id}`,
    );
  }

  // DELETE /api/invitations/delete/{id}
  deleteInvitation(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/invitations/delete/${id}`);
  }

  // GET /api/invitations/token/{token}
  getInvitationByToken(token: string): Observable<InvitationResponse> {
    return this.http.get<InvitationResponse>(
      `${this.apiUrl}/invitations/token/${token}`,
    );
  }

  // POST /api/invitations/register
  registerWithInvitation(
    request: RegisterWithInvitationRequest,
  ): Observable<any> {
    return this.http.post(`${this.apiUrl}/invitations/register`, request);
  }

  // GET /api/roles  — list assignable roles for the invitation form
  listRoles(): Observable<RoleResponse[]> {
    return this.http.get<RoleResponse[]>(`${this.apiUrl}/roles`);
  }
}
